import { SportType } from 'modules/core/domain/enums/sport-type';
import { ChampionshipSettings } from 'modules/championship/domain/interfaces/championship-settings';

/** Из чего складывается матч: основные таймы, дополнительные, серия пенальти. */
export type PeriodKind = 'main' | 'extra' | 'shootout';

export interface PeriodClassification {
  kind: PeriodKind;
  /** Номер внутри своей группы: 1-й основной, 2-й дополнительный. Для серии — 0. */
  ordinal: number;
}

/** Регламент, из которого видно, где кончаются основные таймы и начинаются дополнительные. */
type PeriodsRegulation = Pick<ChampionshipSettings, 'gamePeriodsNumber' | 'gameExtraPeriodsNumber'>;

/**
 * К чему относится номер периода по регламенту чемпионата.
 *
 * Серия пенальти опознаётся двумя способами сразу, потому что в системе она приходит
 * двумя разными числами: протокол матча пишет её нулём
 * (`games/ui/components/game-event-form/game-event-form.tsx`), а счётчик таймов на
 * панели трансляции доходит до неё естественным номером — сразу за последним
 * дополнительным. Оба варианта ниже дают `shootout`.
 */
export function classifyPeriod(period: number, settings?: PeriodsRegulation): PeriodClassification {
  if (period === 0) return { kind: 'shootout', ordinal: 0 };

  const mainCount = settings?.gamePeriodsNumber ?? 0;
  // Без регламента гадать не о чем: считаем номер обычным таймом и печатаем как есть.
  if (mainCount <= 0) return { kind: 'main', ordinal: period };

  if (period <= mainCount) return { kind: 'main', ordinal: period };

  const extraCount = settings?.gameExtraPeriodsNumber ?? 0;
  if (period <= mainCount + extraCount) return { kind: 'extra', ordinal: period - mainCount };

  return { kind: 'shootout', ordinal: 0 };
}

export interface PeriodLabelConfig {
  main: (ordinal: number, period: number) => string;
  extra: (ordinal: number, period: number) => string;
  shootout: (period: number) => string;
}

/**
 * Дефолт печатает исходный номер — ровно то, что табло показывало до появления
 * конфига. Новый вид спорта без своей записи ничего не ломает.
 */
export const DEFAULT_PERIOD_LABELS: PeriodLabelConfig = {
  main: (_ordinal, period) => String(period),
  extra: (_ordinal, period) => String(period),
  shootout: (period) => String(period),
};

export const PERIOD_LABELS_BY_SPORT: Partial<Record<SportType, PeriodLabelConfig>> = {
  [SportType.football]: {
    main: (ordinal) => String(ordinal),
    extra: (ordinal) => `ДВ-${ordinal}`,
    shootout: () => 'Пен.',
  },
};

export function getPeriodLabelConfig(sportType?: SportType | null): PeriodLabelConfig {
  return (sportType && PERIOD_LABELS_BY_SPORT[sportType]) || DEFAULT_PERIOD_LABELS;
}

/** Подпись тайма для табло: «2», «ДВ-1», «Пен.» — в зависимости от вида спорта. */
export function getPeriodLabel(props: { period: number; settings?: PeriodsRegulation; sportType?: SportType | null }): string {
  const { period, settings, sportType } = props;

  const config = getPeriodLabelConfig(sportType);
  const { kind, ordinal } = classifyPeriod(period, settings);

  switch (kind) {
    case 'main':
      return config.main(ordinal, period);
    case 'extra':
      return config.extra(ordinal, period);
    case 'shootout':
      return config.shootout(period);
  }
}
